// Holds the assistant conversation + attachments state ABOVE the panel, so it
// survives the panel being toggled closed and route changes (the Shell stays
// mounted across navigation). Full page reloads rehydrate from the persisted
// conversation_id via useAssistant's history load.
//
// It also brokers "Ask assistant" deep-links from elsewhere in the app (e.g. the
// Dashboard "Needs attention" card): `ask(prompt)` queues a prompt and fires a
// window event the shell listens for to open the panel (the shell owns the open
// state; the panel drains the queued prompt once it is visible). The event name is
// the seam with s6-mobile, which owns the shell open/close wiring.

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { useAssistant, type UseAssistant } from './useAssistant.ts';
import { useAttachments, type UseAttachments } from './useAttachments.ts';

/** Window event the shell listens for to reveal the assistant panel. */
export const ASSISTANT_OPEN_EVENT = 'seencast:assistant-open';

interface AssistantContextValue {
  chat: UseAssistant;
  attach: UseAttachments;
  /** Queue a prompt and ask the shell to open the panel (Dashboard "Ask assistant"). */
  ask: (prompt: string) => void;
  /** Panel drains the queued prompt once visible; returns it and clears the queue. */
  consumePendingAsk: () => string | null;
  /** Increments on every `ask()` so the panel re-checks the queue even when already open. */
  askSignal: number;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

export function AssistantProvider({ children }: { children: ReactNode }) {
  const chat = useAssistant();
  const attach = useAttachments();
  const pendingAsk = useRef<string | null>(null);
  // Bumped on every ask() so the panel re-checks the queue even when already mounted/open.
  const [askSignal, setAskSignal] = useState(0);

  const ask = useCallback((prompt: string) => {
    pendingAsk.current = prompt;
    setAskSignal((n) => n + 1);
    window.dispatchEvent(new CustomEvent(ASSISTANT_OPEN_EVENT));
  }, []);

  const consumePendingAsk = useCallback(() => {
    const p = pendingAsk.current;
    pendingAsk.current = null;
    return p;
  }, []);

  return (
    <AssistantContext.Provider value={{ chat, attach, ask, consumePendingAsk, askSignal }}>
      {children}
    </AssistantContext.Provider>
  );
}

export function useAssistantContext(): AssistantContextValue {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistantContext must be used inside <AssistantProvider>');
  return ctx;
}
